import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, Lock, User, UserPlus, CheckCircle, XCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import Hey from "@/lib/hey-utils";
import { trackAuthAction } from "@/utils/analytics";
import { SUPPORTED_LANGUAGES } from "@/constants/languages";
import { Checkbox } from "@/components/ui/checkbox";

interface RegisterFormProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  email: string;
  setEmail: (email: string) => void;
  confirmEmail: string;
  setConfirmEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (password: string) => void;
  language: string;
  setLanguage: (language: string) => void;
  otpCode: string;
  setOtpCode: (code: string) => void;
  signUpStep: "start" | "otp" | "done";
  setSignUpStep: (step: "start" | "otp" | "done") => void;
  emailsMatch: boolean;
  passwordsMatch: boolean;
  navigate: (path: string) => void;
  isValidEmail: (email: string) => boolean;
}

export const RegisterForm = ({
  isLoading,
  setIsLoading,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  confirmEmail,
  setConfirmEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  language,
  setLanguage,
  otpCode,
  setOtpCode,
  signUpStep,
  setSignUpStep,
  emailsMatch,
  passwordsMatch,
  navigate,
  isValidEmail,
}: RegisterFormProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{}|\\:;"'<>,.?])[a-zA-Z0-9!@#$%^&*()_+\-=[\]{}|\\:;"'<>,.?]{8,15}$/;


  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (signUpStep === "start") {
        if (email !== confirmEmail) {
          throw new Error(t("auth.emailMismatch"));
        }

        trackAuthAction("register_start");
        
        let result = await Hey.HeyUtils.getInstance().registerUser({
          registerSource: "APP",
          user: {
              email: email,
              firstName: firstName,
              lastName: lastName,
              language: language
          }
        });
        if (result?.success)
        {
          trackAuthAction("register_start", "success");
          toast({
            title: t("common.toast.success"),
            description: t("auth.verificationEmailSent"),
          });
          setSignUpStep("otp");
        } else {
          trackAuthAction("register_start", "failed", result.error);
          throw new Error(`${t("common.toast.error")}. ${result.error}`);
        }
      }
      else {
        trackAuthAction("register_complete");
        if (password && password.length > 0 && !regex.test(password)) {
              throw new Error(t("auth.passwordValidation"));
        }
        let result = await Hey.HeyUtils.getInstance().validateOTP(otpCode, email);
        if (result?.success)
        {
          const heyUtils = Hey.HeyUtils.getInstance();
          const notifResult = await heyUtils.enableNotifications(heyUtils.user_id);
          if (password && confirmPassword && password === confirmPassword && password.length > 0) {
            // Optionally, we also set the password
            result = await Hey.HeyUtils.getInstance().setUserPassword(email, password);
            if (result?.success) {
              trackAuthAction("register_complete", "success", "with password");
              toast({
                title: t("common.toast.success"),
                description: t("auth.registrationPasswordSuccess"),
              });
              setSignUpStep("done");
              setTimeout(()=>{navigate("/")}, 1000);
              return
            }
          }
          trackAuthAction("register_complete", "success");
          // toast({
          //   title: t("common.toast.success"),
          //   description: t("auth.loginSuccess"),
          // });
          setSignUpStep("done");
          setTimeout(()=>{navigate("/")}, 1000);
          return 
        } else {
          trackAuthAction("register_complete", "failed", result.error);
          throw new Error(`${t("auth.incorrectVerificationCode")}: ${result.error}`);
        }
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.toast.error"),
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStartStep = () => (
    <div className="space-y-4">
      <div className="relative">
        <Select value={language} onValueChange={setLanguage} disabled={signUpStep !== "start"}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("settings.language.preferred")} />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <SelectItem key={lang.value} value={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="text"
          placeholder={t("auth.firstName")}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="pl-10"
          disabled={signUpStep !== "start"}
          required
        />
      </div>

      <div className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="text"
          placeholder={t("auth.lastName")}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          className="pl-10"
          disabled={signUpStep !== "start"}
          required
        />
      </div>

      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="email"
          placeholder={t("auth.emailAddress")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`pl-10 ${!isValidEmail(email) && email.length > 0 ? 'border-red-500' : ''}`}
          disabled={signUpStep !== "start"}
          required
        />
        {email.length > 0 && !isValidEmail(email) && (
          <p className="mt-1 text-sm text-red-500">{t("auth.invalidEmail")}</p>
        )}
      </div>

      <div className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="email"
          placeholder={t("auth.confirmEmailAddress")}
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          className="pl-10"
          disabled={signUpStep !== "start"}
          required
        />
        {confirmEmail && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {emailsMatch ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
          </div>
        )}
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox 
          id="terms" 
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked === true)}
          disabled={signUpStep !== "start"}
        />
        <label 
          htmlFor="terms" 
          className="text-sm text-gray-600 cursor-pointer"
        >
          {t("auth.termsAgreementPart1")}{" "}
          <a 
            href="https://checkmytrip.com/terms-and-conditions/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-travel-500 underline hover:text-travel-600"
            onClick={() => trackAuthAction("terms_click")}
          >
            {t("auth.termsOfService")}
          </a>
          {" & "}
          <a 
            href="https://checkmytrip.com/privacy-policy/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-travel-500 underline hover:text-travel-600"
            onClick={() => trackAuthAction("terms_click")}
          >
            {t("auth.privacyPolicy")}
          </a>
        </label>
      </div>
    </div>
  );

  const renderOtpStep = () => (
    <div className="space-y-4">
      <div className="space-y-4 flex flex-col items-center">
        <p className="text-sm text-gray-600 text-center">
          {t("auth.enterVerificationCode")} {email}
        </p>
        <Input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={6}
          placeholder={t("auth.enter6DigitCode")}
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="text-center text-lg tracking-widest w-48"
          required
        />
      </div>

      <p className="text-sm text-gray-600 text-center">
        {t("auth.optionalPassword")}
      </p>
      <div className="relative mt-3">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="password"
          placeholder={t("auth.passwordOptional")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="pl-10"
          minLength={6}
        />
      </div>
      <div className="relative mt-3">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
        <Input
          type="password"
          placeholder={t("auth.confirmPassword")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="pl-10"
          minLength={6}
        />
        {confirmPassword && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {passwordsMatch ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <XCircle className="h-5 w-5 text-red-500" />
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderDoneStep = () => (
    <div className="text-center">
      <div className="flex flex-col items-center space-y-4">
        <CheckCircle className="h-16 w-16 text-green-500" />
        <h3 className="text-xl font-semibold text-gray-900">{t("auth.registrationComplete")}</h3>
        <p className="text-gray-600">
          {t("auth.accountCreatedRedirect")}
        </p>
        <div className="animate-pulse bg-travel-100 h-1 w-24 rounded-full" />
      </div>
    </div>
  );

  const needsPasswordValidation = password.length > 0 || confirmPassword.length > 0;
  
  const isFormValid = signUpStep === "start" 
    ? emailsMatch && !isLoading && !(email.length > 0 && !isValidEmail(email)) && termsAccepted
    : signUpStep === "otp" 
      ? !isLoading && otpCode.length === 6 && (!needsPasswordValidation || passwordsMatch)
      : false;

  return (
    <form onSubmit={handleRegistration} className="mt-8 space-y-6">
      <div className="space-y-4">
        {signUpStep === "start" && renderStartStep()}
        {signUpStep === "otp" && renderOtpStep()}
        {signUpStep === "done" && renderDoneStep()}
      </div>

      <Button 
        className="w-full" 
        type="submit" 
        disabled={!isFormValid || signUpStep === "done"}
        style={isFormValid && signUpStep !== "done" ? { backgroundColor: "#000835" } : {}}
      >
        {isLoading ? (
          t("common.updating")
        ) : 
          <>
            <UserPlus className="w-4 h-4 mr-2" />
            {signUpStep === "start" ? (t("auth.createAccount")) :
             signUpStep === "otp" ? (t("auth.verifyEmail")) : (t("auth.registrationComplete"))}
          </>
        }
      </Button>
    </form>
  );
};
